import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Settings, Users, KeyRound } from "lucide-react";
import { Seo } from "../components/Seo";
import { createCheckout, getPlans, getProToken, openProPortal, verifyPro, errorMessage } from "../lib/api";
import { usePro } from "../context/ProContext";

function formatBRL(v) {
    return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function PlanCard({ plan, period, currentPlan, highlight, children, testid }) {
    const price = plan.prices[period];
    return (
        <div
            className={`border-2 border-black p-8 flex flex-col ${highlight ? "bg-black text-white hard-shadow" : "bg-white"}`}
            data-testid={testid}
        >
            <h2 className={`font-poster text-3xl ${highlight ? "text-[#22D3EE]" : "text-black"}`}>{plan.name.toUpperCase()}</h2>
            <p className={`text-xs mt-1 ${highlight ? "text-gray-300" : "text-gray-600"}`}>{plan.tagline}</p>
            <p className={`font-poster text-5xl mt-4 ${highlight ? "text-white" : "text-black"}`}>
                {formatBRL(price)}
                {price > 0 && <span className="text-lg align-top">/{period === "yearly" ? "ano" : "mês"}</span>}
            </p>
            {price > 0 && period === "yearly" && (
                <p className={`font-mono-tag text-[11px] mt-1 ${highlight ? "text-[#22D3EE]" : "text-[#0284C7]"}`}>
                    equivale a {formatBRL(price / 12)}/mês · 2 meses grátis
                </p>
            )}
            <ul className="mt-6 space-y-3 flex-1">
                {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                        <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${highlight ? "text-[#22D3EE]" : "text-[#0284C7]"}`} aria-hidden="true" />
                        <span className={highlight ? "text-gray-200" : "text-gray-800"}>{f}</span>
                    </li>
                ))}
            </ul>
            {plan.notes?.map((n) => (
                <p key={n} className={`mt-3 text-[11px] font-mono-tag ${highlight ? "text-gray-400" : "text-gray-500"}`}>{n}</p>
            ))}
            <div className="mt-8">{children}</div>
        </div>
    );
}

export default function PlansPage() {
    const [period, setPeriod] = useState("monthly");
    const [catalog, setCatalog] = useState(null);
    const [catalogError, setCatalogError] = useState(null);
    const [loading, setLoading] = useState(null); // "pro" | "business"
    const [error, setError] = useState(null);
    const [code, setCode] = useState("");
    const [codeState, setCodeState] = useState(null); // null | checking | error
    const [codeOpen, setCodeOpen] = useState(false);
    const { isPro, isBusiness, plan, role, activate, checking } = usePro();

    useEffect(() => {
        getPlans()
            .then(setCatalog)
            .catch((e) => setCatalogError(errorMessage(e)));
    }, []);

    const subscribe = async (targetPlan) => {
        setLoading(targetPlan);
        setError(null);
        try {
            const { checkout_url } = await createCheckout(targetPlan, period);
            window.location.href = checkout_url;
        } catch (err) {
            setError(errorMessage(err));
            setLoading(null);
        }
    };

    const manage = async () => {
        setLoading("manage");
        try {
            const { portal_url } = await openProPortal(getProToken());
            window.location.href = portal_url;
        } catch {
            setError("Não foi possível abrir o gerenciamento da assinatura. Fale com o suporte.");
            setLoading(null);
        }
    };

    const activateCode = async (e) => {
        e.preventDefault();
        const t = code.trim();
        if (!t) return;
        setCodeState("checking");
        setError(null);
        try {
            const data = await verifyPro(t);
            activate(t, data.plan, data.role);
            setCode("");
            setCodeState(null);
            setCodeOpen(false);
        } catch {
            setCodeState("error");
        }
    };

    const plans = catalog?.plans || [];
    const faq = catalog?.faq || [];
    const planById = Object.fromEntries(plans.map((p) => [p.id, p]));

    return (
        <main className="py-16 px-6 max-w-6xl mx-auto min-h-[60vh]">
            <Seo
                title="Planos Grátis, Pro e Business — ScorpionPDF"
                description="Compare os planos: grátis com 10 operações/dia, Pro a partir de R$ 14,90/mês e Business para equipes de até 5 usuários."
            />
            <span className="font-mono-tag text-xs font-bold text-[#0284C7] uppercase">Planos e assinaturas</span>
            <h1 className="font-poster text-5xl md:text-6xl text-black mt-1">Simples como deve ser</h1>
            <p className="text-gray-700 mt-3 max-w-xl">
                O essencial é grátis para sempre, sem cadastro. Pague só se precisar de mais volume, histórico
                permanente ou uma equipe inteira trabalhando junto.
            </p>

            {/* toggle mensal/anual */}
            <div className="mt-8 inline-flex border-2 border-black" role="group" aria-label="Período de cobrança" data-testid="period-toggle">
                <button
                    onClick={() => setPeriod("monthly")}
                    aria-pressed={period === "monthly"}
                    data-testid="period-monthly"
                    className={`px-6 py-2.5 font-mono-tag text-xs font-bold uppercase transition-colors ${period === "monthly" ? "bg-black text-[#22D3EE]" : "bg-white text-black hover:bg-[#F0F9FF]"}`}
                >
                    Mensal
                </button>
                <button
                    onClick={() => setPeriod("yearly")}
                    aria-pressed={period === "yearly"}
                    data-testid="period-yearly"
                    className={`px-6 py-2.5 font-mono-tag text-xs font-bold uppercase transition-colors border-l-2 border-black ${period === "yearly" ? "bg-black text-[#22D3EE]" : "bg-white text-black hover:bg-[#F0F9FF]"}`}
                >
                    Anual · 2 meses grátis
                </button>
            </div>

            {catalogError && (
                <p className="mt-6 border-2 border-black bg-[#FEF2F2] p-4 text-sm text-red-800" role="alert" data-testid="plans-catalog-error">
                    Não foi possível carregar os planos: {catalogError}
                </p>
            )}

            {!catalog && !catalogError && (
                <p className="mt-10 font-mono-tag text-sm text-gray-600 flex items-center gap-2" data-testid="plans-loading">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Carregando planos…
                </p>
            )}

            {catalog && (
                <div className="grid md:grid-cols-3 gap-6 mt-8">
                    {/* FREE */}
                    <PlanCard plan={planById.free} period={period} highlight={false} testid="plan-free-card">
                        {!isPro ? (
                            <span className="block text-center py-4 border-2 border-black font-mono-tag text-xs font-bold uppercase text-gray-500" data-testid="free-current-badge">
                                Seu plano atual
                            </span>
                        ) : (
                            <span className="block text-center py-4 border-2 border-black font-mono-tag text-xs font-bold uppercase text-gray-500">
                                Plano anterior
                            </span>
                        )}
                        <Link
                            to="/ferramentas"
                            data-testid="free-continue-link"
                            className="block text-center mt-3 py-3 bg-[#0284C7] hover:bg-black text-white font-poster text-lg tracking-wide transition-colors"
                        >
                            CONTINUAR GRÁTIS
                        </Link>
                    </PlanCard>

                    {/* PRO */}
                    <PlanCard plan={planById.pro} period={period} highlight testid="plan-pro-card">
                        {isPro && plan === "pro" ? (
                            <div className="space-y-3">
                                <span className="block text-center py-4 bg-[#0284C7] text-white font-poster text-xl tracking-wide" data-testid="pro-active-badge">
                                    PRO ATIVO NESTE NAVEGADOR
                                </span>
                                {role === "owner" && (
                                    <div className="flex gap-2">
                                        <button
                                            onClick={manage}
                                            disabled={Boolean(loading)}
                                            data-testid="manage-subscription-button"
                                            className="flex-1 py-3 border-2 border-white text-white font-mono-tag text-xs font-bold uppercase hover:bg-white hover:text-black transition-colors inline-flex items-center justify-center gap-2"
                                        >
                                            <Settings className="w-4 h-4" aria-hidden="true" /> Gerenciar assinatura
                                        </button>
                                        <button
                                            onClick={() => activate(null)}
                                            data-testid="remove-pro-button"
                                            className="py-3 px-4 border-2 border-gray-600 text-gray-400 font-mono-tag text-xs font-bold uppercase hover:border-white hover:text-white transition-colors"
                                        >
                                            Remover
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                onClick={() => subscribe("pro")}
                                disabled={Boolean(loading) || checking}
                                data-testid="subscribe-pro-button"
                                className="w-full py-4 bg-[#0284C7] hover:bg-[#22D3EE] disabled:bg-gray-600 text-white font-poster text-2xl tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                            >
                                {loading === "pro" && <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />}
                                {loading === "pro" ? "ABRINDO PAGAMENTO…" : "ASSINAR O PRO"}
                            </button>
                        )}
                    </PlanCard>

                    {/* BUSINESS */}
                    <PlanCard plan={planById.business} period={period} highlight={false} testid="plan-business-card">
                        {isBusiness && role === "owner" ? (
                            <div className="space-y-3">
                                <span className="block text-center py-4 bg-black text-[#22D3EE] font-poster text-xl tracking-wide" data-testid="business-active-badge">
                                    BUSINESS ATIVO
                                </span>
                                <Link
                                    to="/equipe"
                                    data-testid="manage-team-link"
                                    className="flex py-3 border-2 border-black text-black font-mono-tag text-xs font-bold uppercase hover:bg-black hover:text-white transition-colors items-center justify-center gap-2"
                                >
                                    <Users className="w-4 h-4" aria-hidden="true" /> Gerenciar equipe
                                </Link>
                                <div className="flex gap-2">
                                    <button
                                        onClick={manage}
                                        disabled={Boolean(loading)}
                                        data-testid="manage-subscription-button-business"
                                        className="flex-1 py-3 border-2 border-black text-black font-mono-tag text-xs font-bold uppercase hover:bg-black hover:text-white transition-colors inline-flex items-center justify-center gap-2"
                                    >
                                        <Settings className="w-4 h-4" aria-hidden="true" /> Assinatura
                                    </button>
                                    <button
                                        onClick={() => activate(null)}
                                        data-testid="remove-business-button"
                                        className="py-3 px-4 border-2 border-gray-400 text-gray-500 font-mono-tag text-xs font-bold uppercase hover:border-black hover:text-black transition-colors"
                                    >
                                        Remover
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={() => subscribe("business")}
                                disabled={Boolean(loading) || checking}
                                data-testid="subscribe-business-button"
                                className="w-full py-4 bg-black hover:bg-[#0284C7] disabled:bg-gray-400 text-white font-poster text-2xl tracking-wide transition-colors inline-flex items-center justify-center gap-2"
                            >
                                {loading === "business" && <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />}
                                {loading === "business" ? "ABRINDO PAGAMENTO…" : "ASSINAR O BUSINESS"}
                            </button>
                        )}
                    </PlanCard>
                </div>
            )}

            {isPro && plan === "business" && role === "member" && (
                <p className="mt-6 border-2 border-black bg-[#F0F9FF] p-4 text-sm text-gray-800" data-testid="business-member-note">
                    Você usa o ScorpionPDF Business como membro de uma equipe. A assinatura é gerenciada pelo titular.
                </p>
            )}

            {error && (
                <p className="mt-6 border-2 border-black bg-[#FEF2F2] p-4 text-sm text-red-800 font-medium" role="alert" data-testid="plans-error">
                    {error}
                </p>
            )}

            {/* ativar com codigo */}
            <div className="mt-10 border-2 border-black bg-white p-6" data-testid="activate-code-box">
                <button
                    onClick={() => setCodeOpen(!codeOpen)}
                    aria-expanded={codeOpen}
                    data-testid="activate-code-toggle"
                    className="flex items-center gap-2 font-mono-tag text-xs font-bold uppercase text-black hover:text-[#0284C7] transition-colors"
                >
                    <KeyRound className="w-4 h-4" aria-hidden="true" /> Tem um código de acesso? Ative aqui
                </button>
                {codeOpen && (
                    <form onSubmit={activateCode} className="mt-4 flex flex-col sm:flex-row gap-3">
                        <input
                            type="text"
                            value={code}
                            onChange={(e) => { setCode(e.target.value); setCodeState(null); }}
                            placeholder="Cole seu código de acesso"
                            aria-label="Código de acesso"
                            data-testid="activate-code-input"
                            className="flex-1 border-2 border-black bg-white px-3 py-2.5 text-sm font-mono-tag"
                        />
                        <button
                            type="submit"
                            disabled={codeState === "checking" || !code.trim()}
                            data-testid="activate-code-submit"
                            className="px-6 py-2.5 bg-[#0284C7] hover:bg-black disabled:bg-gray-300 text-white font-mono-tag text-xs font-bold uppercase transition-colors inline-flex items-center gap-2"
                        >
                            {codeState === "checking" && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                            Ativar
                        </button>
                    </form>
                )}
                {codeState === "error" && (
                    <p className="mt-3 text-xs text-red-700 font-medium" role="alert" data-testid="activate-code-error">
                        Código inválido ou assinatura inativa. Confira e tente novamente.
                    </p>
                )}
                <p className="mt-3 text-[11px] text-gray-500 font-mono-tag">
                    O código é criado na tela de confirmação do pagamento (ou enviado pelo titular da sua equipe Business) e vincula o plano a este navegador.
                </p>
            </div>

            {/* FAQ */}
            {faq.length > 0 && (
                <section className="mt-14" aria-labelledby="plans-faq">
                    <h2 id="plans-faq" className="font-poster text-3xl text-black">Perguntas frequentes</h2>
                    <div className="mt-6 space-y-3" data-testid="plans-faq-list">
                        {faq.map((item, i) => (
                            <details key={item.q} className="border-2 border-black bg-white group" data-testid={`faq-item-${i}`}>
                                <summary className="cursor-pointer px-5 py-4 font-bold text-sm text-black list-none flex items-center justify-between hover:bg-[#F0F9FF] transition-colors">
                                    {item.q}
                                    <span className="font-poster text-xl text-[#0284C7] group-open:rotate-45 transition-transform">+</span>
                                </summary>
                                <p className="px-5 pb-4 text-sm text-gray-700">{item.a}</p>
                            </details>
                        ))}
                    </div>
                </section>
            )}

            <p className="mt-8 text-xs text-gray-500 font-mono-tag">
                Pagamento processado com segurança pelo Stripe. Cancele quando quiser pelo portal do assinante. Nenhum
                dado de cartão passa pelos nossos servidores.
            </p>
        </main>
    );
}
